import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, Plus, Calendar, CheckCircle, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { apiFetch, ApiError } from "@/lib/api-client";
import {
  useFiscalYears,
  type FiscalYearSummary,
  type FiscalYearStatus,
} from "@/features/fiscal-calendar";

/**
 * Status badge configuration.
 */
const statusConfig: Record<
  FiscalYearStatus,
  { label: string; className: string; icon: typeof CheckCircle }
> = {
  OPEN: {
    label: "Abierto",
    className: "bg-green-100 text-green-800",
    icon: Calendar,
  },
  LOCKED: {
    label: "Bloqueado",
    className: "bg-yellow-100 text-yellow-800",
    icon: Lock,
  },
  CLOSED: {
    label: "Cerrado",
    className: "bg-gray-100 text-gray-800",
    icon: CheckCircle,
  },
};

/**
 * Format date for display.
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString + "T00:00:00");
  return date.toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Fiscal Calendar management page.
 * Lists fiscal years with ability to create new years and navigate to period management.
 */
export function CalendarioFiscalPage() {
  const navigate = useNavigate();
  const { years, isLoading, error, refetch } = useFiscalYears();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newYearNumber, setNewYearNumber] = useState(
    new Date().getFullYear().toString()
  );
  const [isCreating, setIsCreating] = useState(false);

  // Create new fiscal year
  const handleCreateYear = async () => {
    const yearNum = parseInt(newYearNumber, 10);
    if (isNaN(yearNum) || yearNum < 2000 || yearNum > 2100) {
      toast.error("Ingrese un ano valido entre 2000 y 2100");
      return;
    }

    setIsCreating(true);

    try {
      await apiFetch.post("/api/fiscal-calendar/years", {
        yearNumber: yearNum,
      });
      toast.success(`Ano fiscal ${yearNum} creado correctamente`);
      setIsCreateDialogOpen(false);
      refetch();
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message);
      } else {
        toast.error("Error al crear el ano fiscal");
      }
    } finally {
      setIsCreating(false);
    }
  };

  // Navigate to year detail
  const handleViewYear = (year: FiscalYearSummary) => {
    navigate(`/configuracion/calendario-fiscal/${year.id}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-2 text-destructive">
        <AlertCircle className="h-8 w-8" />
        <p>Error al cargar el calendario fiscal</p>
        <p className="text-sm text-muted-foreground">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Calendario Fiscal</h1>
          <p className="text-sm text-muted-foreground">
            Administre los anos y periodos fiscales de su organizacion
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Nuevo Ano Fiscal
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Crear Ano Fiscal</DialogTitle>
              <DialogDescription>
                Se crearan automaticamente 14 periodos: apertura, 12 meses y
                ajustes de cierre.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <label className="text-sm font-medium">Ano</label>
              <Input
                type="number"
                value={newYearNumber}
                onChange={(e) => setNewYearNumber(e.target.value)}
                min={2000}
                max={2100}
                className="mt-1"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button onClick={handleCreateYear} disabled={isCreating}>
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creando...
                  </>
                ) : (
                  "Crear Ano Fiscal"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Years list */}
      {years.length === 0 ? (
        <div className="border rounded-lg p-8 text-center bg-card">
          <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">
            No hay anos fiscales configurados
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Cree su primer ano fiscal para comenzar a registrar asientos
            contables.
          </p>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Crear Ano Fiscal
          </Button>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full">
            <thead>
              <tr className="border-b text-left text-sm text-muted-foreground bg-muted/50">
                <th className="px-4 py-3 font-medium">Ano</th>
                <th className="px-4 py-3 font-medium">Periodo</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 font-medium">Periodos</th>
              </tr>
            </thead>
            <tbody>
              {years.map((year) => {
                const status = statusConfig[year.status];
                const StatusIcon = status.icon;

                return (
                  <tr
                    key={year.id}
                    className="border-b last:border-0 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => handleViewYear(year)}
                  >
                    <td className="px-4 py-3">
                      <span className="font-semibold text-lg">
                        {year.yearNumber}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatDate(year.startDate)} - {formatDate(year.endDate)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full",
                          status.className
                        )}
                      >
                        <StatusIcon className="h-3 w-3" />
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="text-muted-foreground">
                        {year.periodsClosed}/{year.periodsTotal} cerrados
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
